<template>
  <div class="bg-[#F2F6FA] h-full">
    <div class="flex flex-col items-center justify-center w-full pb-20 py-4">
      <section class="flex flex-col items-center w-full justify-center">
        <!-- Header Section -->
        <div
          class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full"
        >
          <div class="flex justify-between items-center w-full">
            <div>
              <div class="items-center gap-2 px-4 -ml-2 d-flex">
                <v-icon
                  class="cursor-pointer text-2xl md:text-[28px]"
                  color="primary"
                >
                  mdi-clipboard-list-outline
                </v-icon>
                <div class="flex items-center gap-1">
                  <p class="text-base md:text-[22px] font-bold">
                    Competitor Summary
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div
          class="flex items-center justify-start mb-3 ml-[48px] md:mb-0 px-2 md:px-0 w-full"
        >
          <div class="items-center gap-2 px-4 d-flex">
            <div class="flex items-center gap-1">
              <p class="text-lg md:text-[18px] text-gray-600">
                Compare your business performance against local competitors
              </p>
            </div>
          </div>
        </div>

        <!-- Location Selector and Filters -->
        <div class="w-full px-4 mb-4">
          <v-card
            class="pa-3"
            :class="{ 'filter-processing': isProcessingFilters }"
          >
            <v-row dense>
              <!-- Location Dropdown -->
              <v-col cols="12" md="5">
                <div class="relative">
                  <div @click="toggleLocationDropdown($event)">
                    <v-text-field
                      :value="
                        selectedStore
                          ? `${
                              selectedStore.gmbProfile?.title ||
                              selectedStore.name
                            } - ${
                              selectedStore.short_address || selectedStore.city
                            }`
                          : 'Select Location'
                      "
                      :label="
                        selectedStore ? 'Selected Location' : 'Select Location'
                      "
                      hide-details
                      dense
                      outlined
                      class="location-input"
                      prepend-inner-icon="mdi-map-marker"
                      readonly
                    >
                      <template #append>
                        <v-icon>{{
                          showLocationDropdown
                            ? "mdi-chevron-up"
                            : "mdi-chevron-down"
                        }}</v-icon>
                      </template>
                    </v-text-field>
                  </div>
                  <div
                    ref="locationDropdown"
                    v-if="showLocationDropdown"
                    class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-60 w-full overflow-y-auto py-2 px-2"
                  >
                    <div
                      v-for="store in locationOptions"
                      :key="store.id"
                      class="p-3 hover:bg-gray-100 cursor-pointer rounded-md mb-1"
                      @click="selectLocation(store)"
                      :class="{
                        'bg-blue-50 border border-blue-200':
                          selectedLocationId === store.id,
                      }"
                    >
                      <div class="flex items-center gap-2">
                        <v-avatar size="32" color="grey lighten-3">
                          <v-icon color="grey darken-2">mdi-store</v-icon>
                        </v-avatar>
                        <div class="flex flex-col w-full">
                          <span class="font-medium text-base">
                            {{ store.gmbProfile?.title || store.name }}
                          </span>
                          <span class="text-xs text-gray-600">
                            {{ store.short_address }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Filters Row -->
              <v-col cols="12" md="7">
                <v-row dense>
                  <!-- Category Filter -->
                  <v-col cols="12" sm="4">
                    <v-select
                      :value="selectedCategories"
                      :items="primaryCategoryOptions"
                      item-text="display_title"
                      item-value="id"
                      label="Primary Category"
                      multiple
                      outlined
                      dense
                      chips
                      deletable-chips
                      hide-details
                      @change="
                        (values) => updateFilters('selectedCategories', values)
                      "
                      :disabled="isProcessingFilters"
                    >
                      <template #selection="{ item, index }">
                        <v-chip
                          v-if="index === 0"
                          x-small
                          close
                          outlined
                          rounded
                          color="primary"
                          @click:close="removeCategory(item.id)"
                        >
                          {{ item.title }}
                        </v-chip>
                        <span v-if="index === 1" class="grey--text caption">
                          (+{{ selectedCategories.length - 1 }})
                        </span>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Sub-Category Filter -->
                  <v-col cols="12" sm="4">
                    <v-select
                      :value="selectedSubCategories"
                      :items="additionalCategoryOptions"
                      item-text="display_title"
                      item-value="id"
                      label="Additional Categories"
                      multiple
                      outlined
                      dense
                      chips
                      deletable-chips
                      hide-details
                      @change="
                        (values) =>
                          updateFilters('selectedSubCategories', values)
                      "
                      :disabled="!categoryOptions.length || isProcessingFilters"
                    >
                      <template #selection="{ item, index }">
                        <v-chip
                          v-if="index === 0"
                          x-small
                          close
                          outlined
                          rounded
                          color="primary"
                          @click:close="removeSubCategory(item.id)"
                        >
                          {{ item.title }}
                        </v-chip>
                        <span v-if="index === 1" class="grey--text caption">
                          (+{{ selectedSubCategories.length - 1 }})
                        </span>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Distance & Clear Filters -->
                  <v-col cols="12" sm="4">
                    <v-row dense>
                      <v-col cols="8">
                        <v-select
                          :value="maxDistance"
                          :items="distanceOptions"
                          label="Distance"
                          outlined
                          dense
                          hide-details
                          @change="
                            (value) => updateFilters('maxDistance', value)
                          "
                          clearable
                          :disabled="isProcessingFilters"
                        >
                          <template #selection="{ item }">
                            <v-chip x-small outlined rounded color="primary">
                              {{ item.text }}
                            </v-chip>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col
                        cols="4"
                        class="d-flex align-center justify-center"
                      >
                        <v-btn
                          v-if="
                            selectedCategories.length ||
                            selectedSubCategories.length ||
                            maxDistance
                          "
                          @click="clearAllFilters"
                          text
                          color="primary"
                          x-small
                          class="text-caption"
                          :disabled="isProcessingFilters"
                        >
                          <v-icon x-small class="mr-1"
                            >mdi-filter-remove</v-icon
                          >
                          Clear
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <!-- Filter Processing Overlay -->
        <div
          v-if="isProcessingFilters"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style="z-index: 9999"
        >
          <div
            class="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-2xl"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
              width="4"
              class="mb-4"
            ></v-progress-circular>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              Applying Filters
            </h3>
            <p class="text-sm text-gray-600">
              Recalculating competitor rankings...
            </p>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="min-h-screen w-full flex flex-col items-center justify-center"
        >
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
              width="4"
            ></v-progress-circular>
            <div class="mt-6">
              <h3 class="text-lg font-medium text-gray-900 mb-2">
                Loading Competitor Data
              </h3>
              <p class="text-sm text-gray-600">
                Analyzing your business performance...
              </p>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div
          v-else-if="
            selectedLocationId &&
            (competitorSummaryData || summaryRanks.length > 0)
          "
          class="w-full px-4"
        >
          <!-- Summary Ranks Section -->
          <v-card class="mb-6" v-if="summaryRanks.length > 0">
            <v-card-title class="text-xl font-bold">
              <v-icon class="mr-2" color="primary">mdi-trophy-outline</v-icon>
              Location Competitive Analysis
              <v-spacer></v-spacer>
              <div class="text-sm text-gray-600 font-normal">
                Based on
                {{ marketOverviewData?.totalCompetitors || 0 }} businesses ({{
                  marketOverviewData?.competitorsOnly || 0
                }}
                competitors + you)
                <span v-if="maxDistance" class="ml-2 text-primary">
                  • Within {{ maxDistance }}km radius
                </span>
                <v-tooltip bottom v-if="getCurrentBusinessData()?.rank">
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      class="ml-2"
                      >mdi-check-circle</v-icon
                    >
                  </template>
                  <span
                    >Using actual competitor data for accurate rankings</span
                  >
                </v-tooltip>
              </div>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="rank in summaryRanks"
                  :key="rank.category"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  xl="2"
                  class="d-flex"
                >
                  <v-card
                    color="white"
                    class="text-center pa-4 position-relative rank-card flex-grow-1"
                    elevation="2"
                  >
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          class="rank-info-icon"
                          size="16"
                          color="grey"
                        >
                          mdi-information
                        </v-icon>
                      </template>
                      <div class="tooltip-content">
                        <div>{{ getRankDescription(rank.category) }}</div>
                        <div
                          v-if="
                            rank.category === 'Categories' &&
                            getRankPerformance(rank.rank, rank.total) ===
                              'Needs Improvement'
                          "
                          class="mt-2 text-warning"
                        >
                          <strong>Missing categories:</strong><br />
                          {{ getMissingCategories() }}
                        </div>
                        <div
                          v-if="
                            rank.category === 'Services' &&
                            getRankPerformance(rank.rank, rank.total) ===
                              'Needs Improvement'
                          "
                          class="mt-2 text-warning"
                        >
                          <strong>Missing services:</strong><br />
                          {{ getMissingServices() }}
                        </div>
                      </div>
                    </v-tooltip>

                    <div
                      class="text-h4 font-weight-bold"
                      :style="{ color: getRankColor(rank.rank, rank.total) }"
                    >
                      #{{ rank.rank }}/{{ rank.total }}
                    </div>
                    <div class="text-subtitle-1 mt-2 text-grey-darken-2">
                      {{ rank.category }}
                    </div>
                    <div
                      class="text-caption mt-1 font-weight-medium"
                      :style="{ color: getRankColor(rank.rank, rank.total) }"
                    >
                      {{ getRankPerformance(rank.rank, rank.total) }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- High Level Summary -->
          <v-card class="mb-6" v-if="marketOverviewData">
            <v-card-title class="text-xl font-bold">
              <v-icon class="mr-2" color="primary">mdi-chart-box</v-icon>
              Market Overview
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.competitorsOnly }}
                    </div>
                    <div class="text-sm text-gray-600">Total Competitors</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.averageRating }}
                    </div>
                    <div class="text-sm text-gray-600">Average Rating</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.averageReviews }}
                    </div>
                    <div class="text-sm text-gray-600">Average Reviews</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.nearestDistance }}
                    </div>
                    <div class="text-sm text-gray-600">Nearest</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.farthestDistance }}
                    </div>
                    <div class="text-sm text-gray-600">Farthest</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" md="2">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-primary">
                      {{ marketStats.yourShareOfVoice }}%
                    </div>
                    <div class="text-sm text-gray-600">Your Share of Voice</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Detailed Comparison Table -->
          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <div>
                <v-icon class="mr-2" color="primary">mdi-table-large</v-icon>
                <span class="text-xl font-bold">Detailed Comparison</span>
              </div>
              <v-text-field
                v-model="searchQuery"
                append-icon="mdi-magnify"
                label="Search businesses..."
                single-line
                hide-details
                dense
                outlined
                class="ml-4"
                style="max-width: 300px"
                :disabled="isLoadingTable"
                @input="onSearchInput"
                clearable
              ></v-text-field>
            </v-card-title>
            <v-card-text>
              <!-- Table Loading State -->
              <div v-if="isLoadingTable" class="text-center py-12">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="48"
                  width="4"
                  class="mb-4"
                ></v-progress-circular>
                <h3 class="text-lg font-medium text-gray-900 mb-2">
                  Loading competitors..
                </h3>
                <!-- <p class="text-sm text-gray-600">
                  Calculating competitor rankings and market analysis...
                </p> -->
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else-if="tableDataReady"
                :headers="tableHeaders"
                :items="deferredTableData"
                :search="searchQuery"
                :sort-by="['isCurrentBusiness', 'overall_rank']"
                :sort-desc="[true, false]"
                :custom-sort="customSort"
                class="elevation-1"
                :items-per-page="50"
                item-key="id"
                :footer-props="{
                  'items-per-page-options': [25, 50, 100, -1],
                  showFirstLastPage: true,
                  showCurrentPage: true,
                }"
                :item-class="getItemClass"
                :loading="competitorPagination.isLoadingMore"
                loading-text="Loading more competitors..."
                :hide-default-footer="false"
              >
                <!-- Business Name with Image -->
                <template v-slot:[`item.title`]="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar size="40" class="mr-3" color="grey lighten-3">
                      <img
                        v-if="item.icon"
                        :src="item.icon"
                        :alt="item.title"
                        @error="handleImageError($event, item)"
                        style="object-fit: cover; width: 100%; height: 100%"
                      />
                      <v-icon v-else color="grey darken-1">mdi-domain</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">
                        {{ item.title }}
                        <v-chip
                          v-if="item.isCurrentBusiness"
                          x-small
                          color="primary"
                          text-color="white"
                          class="ml-2"
                        >
                          Your Business
                        </v-chip>
                      </div>
                      <div class="caption text--secondary">
                        {{
                          item.category && item.category[0]
                            ? item.category[0].title
                            : "N/A"
                        }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Overall Rank Badge -->
                <template v-slot:[`item.overall_rank`]="{ item }">
                  <span
                    :style="{
                      color: getRankTextColor(item.overall_rank),
                      fontWeight: 'bold',
                    }"
                    class="text-body-2"
                  >
                    #{{ item.overall_rank || "N/A" }}
                  </span>
                </template>

                <!-- Rating with Stars -->
                <template v-slot:[`item.rating`]="{ item }">
                  <div class="d-flex align-center">
                    <v-rating
                      :value="item.rating || 0"
                      color="amber"
                      dense
                      half-increments
                      readonly
                      size="16"
                    ></v-rating>
                    <span class="ml-2 text-sm">{{ item.rating || "N/A" }}</span>
                  </div>
                </template>

                <!-- Reviews Count -->
                <template v-slot:[`item.reviews_cnt`]="{ item }">
                  <span
                    :style="{
                      color: getReviewsTextColor(item.reviews_cnt),
                      fontWeight: 'bold',
                    }"
                    class="text-body-2"
                  >
                    {{ item.reviews_cnt || 0 }} reviews
                  </span>
                </template>

                <!-- Share of Voice -->
                <template v-slot:[`item.share_of_voice`]="{ item }">
                  <div class="text-center">
                    <div class="font-weight-bold">
                      {{ item.share_of_voice.toFixed(1) }}%
                    </div>
                    <div class="text-xs text-gray-500">
                      of {{ getTotalMarketReviews() }} total
                    </div>
                  </div>
                </template>

                <!-- Rank Badge -->
                <template v-slot:[`item.rank`]="{ item }">
                  <span
                    :style="{
                      color: getRankTextColor(item.rank),
                      fontWeight: 'bold',
                    }"
                    class="text-body-2"
                  >
                    #{{ item.rank || "N/A" }}
                  </span>
                </template>

                <!-- Categories -->
                <template v-slot:[`item.categories`]="{ item }">
                  <v-tooltip
                    bottom
                    v-if="item.category && item.category.length > 0"
                  >
                    <template #activator="{ on, attrs }">
                      <div class="d-flex flex-wrap" v-bind="attrs" v-on="on">
                        <!-- Primary Category (first one) -->
                        <v-chip
                          v-if="item.category && item.category[0]"
                          x-small
                          outlined
                          color="blue"
                          class="mr-1 mb-1 primary-category-chip"
                        >
                          {{ item.category[0].title }}
                        </v-chip>
                        <!-- Secondary Categories -->
                        <v-chip
                          v-for="(cat, index) in (item.category || []).slice(
                            1,
                            3
                          )"
                          :key="index + 1"
                          x-small
                          outlined
                          color="grey"
                          class="mr-1 mb-1 secondary-category-chip"
                        >
                          {{ cat.title }}
                        </v-chip>
                        <!-- More indicator -->
                        <v-chip
                          v-if="item.category && item.category.length > 3"
                          x-small
                          outlined
                          color="grey"
                          class="mr-1 mb-1"
                        >
                          +{{ item.category.length - 3 }}
                        </v-chip>
                      </div>
                    </template>
                    <div class="tooltip-content">
                      <div class="font-weight-bold mb-2" style="color: white">
                        All Categories ({{ item.category.length }}):
                      </div>
                      <div
                        v-for="(cat, index) in item.category"
                        :key="index"
                        class="mb-1"
                      >
                        <span
                          v-if="index === 0"
                          style="color: white; font-weight: bold"
                          >• {{ cat.title }} (Primary)</span
                        >
                        <span v-else style="color: white"
                          >• {{ cat.title }}</span
                        >
                      </div>
                    </div>
                  </v-tooltip>
                  <div v-else class="d-flex flex-wrap">
                    <span class="text-body-2 text--secondary"
                      >No categories</span
                    >
                  </div>
                </template>

                <!-- Service Options -->
                <template v-slot:[`item.amenities`]="{ item }">
                  <v-tooltip bottom v-if="getAmenities(item.tags).length > 0">
                    <template #activator="{ on, attrs }">
                      <div class="d-flex flex-wrap" v-bind="attrs" v-on="on">
                        <span
                          v-for="(amenity, index) in getAmenities(
                            item.tags
                          ).slice(0, 3)"
                          :key="index"
                          class="text-body-2 mr-2 mb-1"
                          style="color: #2e7d32; font-weight: 500"
                        >
                          {{ amenity }}
                        </span>
                        <span
                          v-if="getAmenities(item.tags).length > 3"
                          class="text-body-2 mr-2 mb-1"
                          style="color: #757575; font-weight: 500"
                        >
                          +{{ getAmenities(item.tags).length - 3 }}
                        </span>
                      </div>
                    </template>
                    <div class="tooltip-content">
                      <div class="font-weight-bold mb-2">
                        All Amenities ({{ getAmenities(item.tags).length }}):
                      </div>
                      <div
                        v-for="(amenity, index) in getAmenities(item.tags)"
                        :key="index"
                        class="mb-1"
                      >
                        • {{ amenity }}
                      </div>
                    </div>
                  </v-tooltip>
                  <div v-else class="d-flex flex-wrap">
                    <span class="text-body-2 text--secondary"
                      >No amenities</span
                    >
                  </div>
                </template>

                <!-- Status -->
                <template v-slot:[`item.status`]="{ item }">
                  <span
                    :style="{
                      color: item.claimed ? '#2e7d32' : '#f57c00',
                      fontWeight: 'bold',
                    }"
                    class="text-body-2"
                  >
                    {{ item.claimed ? "Claimed" : "Unclaimed" }}
                  </span>
                </template>

                <!-- Actions -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    v-if="item.map_link"
                    :href="item.map_link"
                    target="_blank"
                    small
                    color="primary"
                    outlined
                  >
                    <v-icon small class="mr-1">mdi-map</v-icon>
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </div>

        <!-- No Data State -->
        <div
          v-else-if="
            selectedLocationId &&
            !competitorSummaryData &&
            !summaryRanks.length &&
            !isLoading
          "
          class="w-full px-4 text-center"
        >
          <v-card class="pa-8">
            <v-icon size="64" color="grey">mdi-information-outline</v-icon>
            <h3 class="text-h6 mt-4 mb-2">No Competitor Data Available</h3>
            <p class="text-body-1 text--secondary">
              No competitors found for the selected location. Competitor data
              may not be available yet.
            </p>
          </v-card>
        </div>

        <!-- Select Location State -->
        <div
          v-else-if="!selectedLocationId && !isLoading"
          class="w-full px-4 text-center"
        >
          <v-card class="pa-8">
            <v-icon size="64" color="primary">mdi-map-marker-outline</v-icon>
            <h3 class="text-h6 mt-4 mb-2">Select a Location</h3>
            <p class="text-body-1 text--secondary">
              Please select a location from the dropdown above to view
              competitor analysis.
            </p>
          </v-card>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "@/utils/firebase.utils";
import commonMixins from "@/mixins/common.mixins";

export default {
  name: "CompetitorSummary",
  mixins: [commonMixins],
  data() {
    return {
      selectedLocationId: null,
      selectedStore: null,
      competitors: [],
      allCompetitors: [], // Store all competitors before filtering
      isLoading: false,
      loadingLocations: false,
      searchQuery: "",
      locationSearch: "",
      summaryRanks: [],
      showLocationDropdown: false,
      selectedCategories: [],
      selectedSubCategories: [],
      maxDistance: null,
      baseDistanceOptions: [
        { text: "1 km", value: 1 },
        { text: "2 km", value: 2 },
        { text: "5 km", value: 5 },
        { text: "10 km", value: 10 },
        { text: "15 km", value: 15 },
        { text: "25 km", value: 25 },
        { text: "50 km", value: 50 },
        { text: "100 km", value: 100 },
      ],
      // Performance optimization caches
      categoryOptionsCache: [],
      subCategoryOptionsCache: [],
      distanceOptionsCache: [],
      distanceCalculationsCache: new Map(),
      filterChangeTimeout: null,
      isProcessingFilters: false,
      // Performance optimization - memoization caches
      memoizedFilters: {
        lastSearchQuery: "",
        lastFilterHash: "",
        lastFilteredResult: [],
        lastTableDataHash: "",
        lastTableDataResult: [],
      },
      // Debounced search
      searchTimeout: null,
      searchDebounceMs: 300,
      // New properties for competitor summary data
      competitorSummaryData: null,
      marketOverviewData: null,
      selectedDate: null, // Will store the latest available date from server
      // Loading states
      isLoadingTable: false, // Separate loading state for table
      tableDataReady: false, // Flag to indicate if table data is ready
      deferredTableData: [], // Store computed table data
      // Simple business placeholder as data URL
      businessPlaceholder:
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIgc3Ryb2tlPSIjZTBlMGUwIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIxMiIgZmlsbD0iIzllOWU5ZSIvPgogIDxyZWN0IHg9IjMwIiB5PSI1NSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjI1IiByeD0iMiIgZmlsbD0iIzllOWU5ZSIvPgogIDx0ZXh0IHg9IjUwIiB5PSI5MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjgiIGZpbGw9IiM3NTc1NzUiPkJ1c2luZXNzPC90ZXh0Pgo8L3N2Zz4K",
      // Pagination state
      competitorPagination: {
        pageSize: 10, // Number of competitors to load per batch
        currentPage: 1,
        totalPages: 1,
        totalCompetitors: 0,
        hasMore: true,
        lastDocument: null, // For Firestore cursor pagination
        isLoadingMore: false,
      },
      tableHeaders: [
        {
          text: "Business Name",
          value: "title",
          align: "start",
          sortable: true,
          width: "200px",
        },
        {
          text: "Overall Rank",
          value: "overall_rank",
          align: "center",
          sortable: true,
          width: "120px",
        },
        {
          text: "Search Rank",
          value: "rank",
          align: "center",
          sortable: true,
          width: "120px",
        },
        {
          text: "Rating",
          value: "rating",
          align: "center",
          sortable: true,
          width: "150px",
        },
        {
          text: "Reviews",
          value: "reviews_cnt",
          align: "center",
          sortable: true,
          width: "120px",
        },
        {
          text: "Share of Voice",
          value: "share_of_voice",
          align: "center",
          sortable: true,
          width: "140px",
        },
        {
          text: "Categories",
          value: "categories",
          align: "start",
          sortable: false,
          width: "200px",
        },
        {
          text: "Amenities",
          value: "amenities",
          align: "start",
          sortable: false,
          width: "200px",
        },
        {
          text: "Status",
          value: "status",
          align: "center",
          sortable: true,
          width: "100px",
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          width: "100px",
        },
      ],
    };
  },
  computed: {
    locationOptions() {
      return this.globalStores.map((store) => ({
        ...store,
        display_name: `${store.gmbProfile?.title || store.name || ""} - ${
          store.sub_locality || store.locality || store.city || ""
        }`,
        short_address: `${
          store.sub_locality || store.locality || store.city || ""
        }, ${store.city || store.state || ""}`,
      }));
    },
    primaryCategoryOptions() {
      // Return cached version if available and data hasn't changed
      if (this.categoryOptionsCache.length > 0) {
        return this.categoryOptionsCache;
      }

      const categories = new Map();
      this.allCompetitors.forEach((comp) => {
        if (
          comp.category &&
          Array.isArray(comp.category) &&
          comp.category.length > 0
        ) {
          const primaryCat = comp.category[0]; // Get primary category
          if (primaryCat.title) {
            const id =
              primaryCat.id ||
              primaryCat.title.toLowerCase().replace(/\s+/g, "_");
            if (categories.has(id)) {
              categories.set(id, {
                ...categories.get(id),
                count: categories.get(id).count + 1,
              });
            } else {
              categories.set(id, {
                id: id,
                title: primaryCat.title,
                count: 1,
              });
            }
          }
        }
      });

      const result = Array.from(categories.values()).map((cat) => ({
        ...cat,
        display_title: `${cat.title} (${cat.count})`,
      }));

      return result;
    },
    additionalCategoryOptions() {
      // Return cached version if available and data hasn't changed
      if (this.subCategoryOptionsCache.length > 0) {
        return this.subCategoryOptionsCache;
      }

      const additionalCategories = new Map();
      this.allCompetitors.forEach((comp) => {
        if (comp.category && Array.isArray(comp.category)) {
          comp.category.slice(1).forEach((cat) => {
            // Skip first category as it's primary
            if (cat.title) {
              const id = cat.id || cat.title.toLowerCase().replace(/\s+/g, "_");
              if (additionalCategories.has(id)) {
                additionalCategories.set(id, {
                  ...additionalCategories.get(id),
                  count: additionalCategories.get(id).count + 1,
                });
              } else {
                additionalCategories.set(id, {
                  id: id,
                  title: cat.title,
                  count: 1,
                });
              }
            }
          });
        }
      });

      const result = Array.from(additionalCategories.values()).map((cat) => ({
        ...cat,
        display_title: `${cat.title} (${cat.count})`,
      }));

      return result;
    },
    distanceOptions() {
      // Return cached version if available
      if (this.distanceOptionsCache.length > 0) {
        return this.distanceOptionsCache;
      }

      const currentBusiness = this.getCurrentBusinessData();
      if (
        !currentBusiness ||
        !currentBusiness.latitude ||
        !currentBusiness.longitude
      ) {
        return this.baseDistanceOptions;
      }

      // Pre-calculate all distances once and cache them
      const competitorDistances = this.getCompetitorDistances(currentBusiness);

      const result = this.baseDistanceOptions.map((option) => {
        const count = competitorDistances.filter(
          (dist) => dist <= option.value
        ).length;
        return {
          ...option,
          text: `${option.text} (${count})`,
        };
      });

      return result;
    },
    filteredCompetitors() {
      let filtered = [...this.competitors];

      // Exclude current business from competitors if found in the list
      const currentPlaceId = this.currentBusinessPlaceId;
      if (currentPlaceId) {
        filtered = filtered.filter(
          (comp) =>
            comp.map_id_encoded !== currentPlaceId &&
            comp.place_id !== currentPlaceId &&
            comp.fid !== currentPlaceId
        );
      }

      // Apply distance filter with cached distances
      if (this.maxDistance) {
        const currentBusiness = this.getCurrentBusinessData();
        if (
          currentBusiness &&
          currentBusiness.latitude &&
          currentBusiness.longitude
        ) {
          filtered = filtered.filter((comp) => {
            if (!comp.latitude || !comp.longitude) return false;

            // Get cached distance for this specific competitor
            const distance = this.getCachedDistance(currentBusiness, comp);
            return distance <= this.maxDistance;
          });
        }
      }

      // Apply category filters
      if (this.selectedCategories.length > 0) {
        filtered = filtered.filter((comp) => {
          if (!comp.category || !Array.isArray(comp.category)) return false;
          return comp.category.some((cat) =>
            this.selectedCategories.includes(
              cat.id || cat.title.toLowerCase().replace(/\s+/g, "_")
            )
          );
        });
      }

      // Apply sub-category filters
      if (this.selectedSubCategories.length > 0) {
        filtered = filtered.filter((comp) => {
          if (!comp.category || !Array.isArray(comp.category)) return false;
          return comp.category.some((cat) =>
            this.selectedSubCategories.includes(
              cat.id || cat.title.toLowerCase().replace(/\s+/g, "_")
            )
          );
        });
      }

      // Apply search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (comp) =>
            comp.title?.toLowerCase().includes(query) ||
            comp.address?.toLowerCase().includes(query) ||
            (comp.category &&
              comp.category.some((cat) =>
                cat.title?.toLowerCase().includes(query)
              ))
        );
      }

      return filtered;
    },
    currentBusinessPlaceId() {
      return (
        this.selectedStore?.place_id ||
        this.selectedStore?.gmbProfile?.place_id ||
        null
      );
    },
    tableData() {
      // Return pre-calculated deferred table data
      return this.deferredTableData;
    },
    marketStats() {
      if (!this.marketOverviewData) {
        return {
          totalCompetitors: 0,
          competitorsOnly: 0,
          averageRating: "0.00",
          averageReviews: "0.00",
          nearestDistance: "N/A",
          farthestDistance: "N/A",
          yourShareOfVoice: "0.00",
        };
      }

      return {
        totalCompetitors: this.marketOverviewData.totalCompetitors || 0,
        competitorsOnly: this.marketOverviewData.competitorsOnly || 0,
        averageRating: this.marketOverviewData.averageRating
          ? this.marketOverviewData.averageRating.toFixed(2)
          : "0.00",
        averageReviews: this.marketOverviewData.averageReviews
          ? this.marketOverviewData.averageReviews.toFixed(0)
          : "0",
        nearestDistance: this.marketOverviewData.nearestCompetitor
          ? `${this.marketOverviewData.nearestCompetitor.distance.toFixed(
              2
            )} km`
          : "N/A",
        farthestDistance: this.marketOverviewData.farthestCompetitor
          ? `${this.marketOverviewData.farthestCompetitor.distance.toFixed(
              2
            )} km`
          : "N/A",
        yourShareOfVoice: this.marketOverviewData.yourShareOfVoice
          ? this.marketOverviewData.yourShareOfVoice.toFixed(1)
          : "0.0",
      };
    },
    categoryOptions() {
      // Return primaryCategoryOptions for backward compatibility
      return this.primaryCategoryOptions;
    },
  },
  async mounted() {
    // Initialize caches and states
    this.clearCaches();
    this.isProcessingFilters = false;

    // Auto-select first location if available
    if (this.globalStores.length > 0) {
      const firstStore = this.globalStores[0];
      this.selectedLocationId = firstStore.id;
      this.selectedStore = {
        ...firstStore,
        short_address: `${
          firstStore.sub_locality ||
          firstStore.locality ||
          firstStore.city ||
          ""
        }, ${firstStore.city || firstStore.state || ""}`,
      };
      this.locationSearch =
        firstStore.gmbProfile?.title || firstStore.name || "";
      await this.onLocationChange();
    }

    // Add click outside listener for dropdown
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove event listeners
    document.removeEventListener("click", this.handleClickOutside);

    // Clear all timeouts to prevent memory leaks
    if (this.filterChangeTimeout) {
      clearTimeout(this.filterChangeTimeout);
      this.filterChangeTimeout = null;
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // Reset processing states
    this.isProcessingFilters = false;
    this.isLoadingTable = false;
    this.tableDataReady = false;
    this.competitorPagination.isLoadingMore = false;

    // Clear all caches and memoized data to prevent memory leaks
    this.clearCaches();
    this.memoizedFilters = {
      lastSearchQuery: "",
      lastFilterHash: "",
      lastFilteredResult: [],
      lastTableDataHash: "",
      lastTableDataResult: [],
    };

    // Clear large arrays
    this.deferredTableData = [];
    this.competitors = [];
    this.allCompetitors = [];
    this.summaryRanks = [];
  },
  methods: {
    getCurrentDateString() {
      const now = new Date();
      return now.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
    },
    getParentWorkspaceId() {
      // Return the parent workspace ID from the current workspace
      // This assumes the current workspace has a parent workspace ID
      const currentWorkspace = this.globalStores.find(
        (store) => store.id === this.selectedLocationId
      );
      return currentWorkspace?.parentWorkspaceId || this.currentUID; // Fallback to user ID if no parent workspace
    },
    toggleLocationDropdown(event) {
      if (event) {
        event.stopPropagation();
      }
      this.showLocationDropdown = !this.showLocationDropdown;
    },
    handleClickOutside(event) {
      const dropdown = this.$refs.locationDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.showLocationDropdown = false;
      }
    },
    selectLocation(store) {
      this.selectedStore = {
        ...store,
        short_address: `${
          store.sub_locality || store.locality || store.city || ""
        }, ${store.city || store.state || ""}`,
      };
      this.selectedLocationId = store.id;
      this.locationSearch = store.gmbProfile?.title || store.name || "";
      this.showLocationDropdown = false;
      this.onLocationChange();
    },
    removeCategory(categoryId) {
      const newValues = this.selectedCategories.filter(
        (id) => id !== categoryId
      );
      this.updateFilters("selectedCategories", newValues);
    },
    removeSubCategory(subCategoryId) {
      const newValues = this.selectedSubCategories.filter(
        (id) => id !== subCategoryId
      );
      this.updateFilters("selectedSubCategories", newValues);
    },
    updateFilters(filterName, values) {
      this.isProcessingFilters = true;
      setTimeout(() => {
        this[filterName] = values;
        this.onFiltersChange();
      }, 10);
    },
    onFiltersChange() {
      // Pause progressive loading when filters change
      this.competitorPagination.isLoadingMore = false;

      // Debounce filter changes to improve performance
      if (this.filterChangeTimeout) {
        clearTimeout(this.filterChangeTimeout);
      }

      this.filterChangeTimeout = setTimeout(async () => {
        try {
          // Recalculate table data with filtered data if table is ready
          if (this.tableDataReady) {
            this.tableDataReady = false;
            await this.calculateTableData();
            this.tableDataReady = true;
          }

          // Resume progressive loading if more data is available and no active filters
          if (
            this.competitorPagination.hasMore &&
            !this.selectedCategories.length &&
            !this.selectedSubCategories.length &&
            !this.maxDistance &&
            !this.searchQuery.trim()
          ) {
            // Small delay before resuming progressive loading
            setTimeout(() => {
              this.loadMoreCompetitors();
            }, 1000);
          }
        } finally {
          // Hide processing animation
          this.isProcessingFilters = false;
        }
      }, 150); // 150ms debounce
    },
    clearAllFilters() {
      this.isProcessingFilters = true;

      setTimeout(() => {
        this.selectedCategories = [];
        this.selectedSubCategories = [];
        this.maxDistance = null;

        // Clear filter-related caches to force recalculation
        this.distanceOptionsCache = [];

        this.onFiltersChange();
      }, 10);
    },
    getActiveFiltersCount() {
      let count = 0;
      if (this.selectedCategories.length) count++;
      if (this.selectedSubCategories.length) count++;
      if (this.maxDistance) count++;
      return count;
    },
    getCompetitorDistances(currentBusiness) {
      // Create a cache key based on current business location
      const cacheKey = `distances_${currentBusiness.latitude}_${currentBusiness.longitude}`;

      // Return cached distances if available
      if (this.distanceCalculationsCache.has(cacheKey)) {
        return this.distanceCalculationsCache.get(cacheKey);
      }

      // Calculate distances for all valid competitors
      const distances = [];

      for (const comp of this.allCompetitors) {
        // Skip invalid coordinates or current business
        if (
          !comp.latitude ||
          !comp.longitude ||
          comp.map_id_encoded === this.currentBusinessPlaceId ||
          comp.place_id === this.currentBusinessPlaceId ||
          comp.fid === this.currentBusinessPlaceId
        ) {
          continue;
        }

        // Use individual caching for each distance calculation
        const distance = this.getCachedDistance(currentBusiness, comp);
        distances.push(distance);
      }

      // Cache the array of distances
      this.distanceCalculationsCache.set(cacheKey, distances);

      return distances;
    },
    getCachedDistance(currentBusiness, competitor) {
      // Create individual cache key for this specific distance calculation
      const cacheKey = `${currentBusiness.latitude}_${currentBusiness.longitude}_${competitor.latitude}_${competitor.longitude}`;

      // Return cached distance if available
      if (this.distanceCalculationsCache.has(cacheKey)) {
        return this.distanceCalculationsCache.get(cacheKey);
      }

      // Calculate and cache the distance
      const distance = this.calculateDistance(
        currentBusiness.latitude,
        currentBusiness.longitude,
        competitor.latitude,
        competitor.longitude
      );

      this.distanceCalculationsCache.set(cacheKey, distance);
      return distance;
    },
    clearCaches() {
      // Clear all performance caches when data changes
      this.categoryOptionsCache = [];
      this.subCategoryOptionsCache = [];
      this.distanceOptionsCache = [];
      this.distanceCalculationsCache.clear();

      // Clear memoization caches
      this.memoizedFilters = {
        lastSearchQuery: "",
        lastFilterHash: "",
        lastFilteredResult: [],
        lastTableDataHash: "",
        lastTableDataResult: [],
      };
    },
    getConsistentBusinessDataset() {
      // This method ensures both summary ranks and table data use the same dataset
      const currentBusiness = this.getCurrentBusinessData();
      let competitors = [...this.filteredCompetitors];

      // Always include current business, even if it doesn't match filters
      if (currentBusiness) {
        // Remove current business from competitors if it exists there (to avoid duplication)
        competitors = competitors.filter((comp) => !comp.isCurrentBusiness);
      }

      return currentBusiness ? [currentBusiness, ...competitors] : competitors;
    },
    setCachesFromFirestoreData() {
      // Set caches based on Firestore data to improve performance
      if (this.competitorSummaryData) {
        // Clear existing caches to force recalculation with new data
        this.clearCaches();
      }
    },
    async onLocationChange() {
      if (!this.selectedLocationId) return;

      this.isLoading = true;
      this.isLoadingTable = false;
      this.tableDataReady = false;
      this.deferredTableData = [];
      this.isProcessingFilters = false;
      this.selectedCategories = [];
      this.selectedSubCategories = [];
      this.maxDistance = null;

      // Reset pagination state
      this.resetPagination();

      if (this.filterChangeTimeout) {
        clearTimeout(this.filterChangeTimeout);
        this.filterChangeTimeout = null;
      }

      // Defer heavy work to next tick to ensure loader is shown
      setTimeout(async () => {
        try {
          // Fetch the summary data first (this shows summary cards immediately)
          await this.fetchCompetitors();

          // Hide main loading state as soon as summary data is loaded
          this.isLoading = false;

          // Start loading table data in background
          this.loadTableDataInBackground();
        } catch (error) {
          console.error("Error loading competitor data:", error);
          this.$store.dispatch("alert/show", {
            type: "error",
            message: "Failed to load competitor data. Please try again.",
          });
          this.isLoading = false;
        }
      }, 50); // Small delay to ensure UI updates
    },

    async loadTableDataInBackground() {
      // Show table loading state
      this.isLoadingTable = true;

      // Use setTimeout to ensure UI updates
      setTimeout(async () => {
        try {
          // Calculate table data with heavy computations
          await this.calculateTableData();

          // Mark table as ready
          this.tableDataReady = true;
          this.isLoadingTable = false;

          // Start automatic progressive loading if more data is available
          if (this.competitorPagination.hasMore) {
            // Small delay before starting progressive loading
            setTimeout(() => {
              this.loadMoreCompetitors();
            }, 500); // 500ms delay before starting background loading
          }
        } catch (error) {
          console.error("Error calculating table data:", error);
          this.isLoadingTable = false;
          // Show empty table if calculations fail
          this.deferredTableData = [];
          this.tableDataReady = true;
        }
      }, 100); // Small delay to show loading state
    },

    async calculateTableData() {
      // Use lightweight, non-blocking calculation for initial table data
      try {
        const currentBusiness = this.getCurrentBusinessData();
        let competitors = [...this.filteredCompetitors];

        // Remove current business from competitors if it exists there (to avoid duplication)
        if (currentBusiness) {
          competitors = competitors.filter((comp) => !comp.isCurrentBusiness);
        }

        // Start with current business if available
        let initialTableData = [];
        if (currentBusiness) {
          const enrichedCurrentBusiness = {
            ...currentBusiness,
            // Ensure unique ID for v-data-table item-key
            id: currentBusiness.id || currentBusiness.place_id || currentBusiness.map_id_encoded || currentBusiness.fid || `current_business_${Date.now()}`,
            share_of_voice: this.calculateShareOfVoiceFast(currentBusiness),
            overall_rank: this.competitorSummaryData?.overallRank || 1,
          };
          initialTableData = [enrichedCurrentBusiness];
        }

        // Set initial data immediately for responsive UI
        this.deferredTableData = initialTableData;

        // Process competitors in background chunks
        await this.processAllCompetitorsInBackground(competitors);
      } catch (error) {
        console.error("Error in table data calculation:", error);
        this.deferredTableData = [];
      }
    },

    async processAllCompetitorsInBackground(competitors) {
      const CHUNK_SIZE = 10; // Process 10 competitors at a time

      for (let i = 0; i < competitors.length; i += CHUNK_SIZE) {
        const chunk = competitors.slice(i, i + CHUNK_SIZE);

        // Process chunk with lightweight calculations
        const processedChunk = chunk.map((comp) => ({
          ...comp,
          // Ensure unique ID for v-data-table item-key
          id: comp.id || comp.place_id || comp.map_id_encoded || comp.fid || `competitor_${Date.now()}_${Math.random()}`,
          // Use simple, fast calculations instead of heavy ones
          share_of_voice: this.calculateShareOfVoiceFast(comp),
          overall_rank: this.calculateRankFast(comp),
        }));

        // Append to existing table data
        this.deferredTableData = [...this.deferredTableData, ...processedChunk];

        // Yield control back to UI thread every chunk
        await this.yieldToUI();
      }
    },

    async fetchCompetitors() {
      try {
        // Get the latest available competitor summary data
        const competitorSummaryRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "competitor-summary"
        );

        // Get all available dates and find the latest one
        const datesSnapshot = await getDocs(competitorSummaryRef);

        if (datesSnapshot.empty) {
          console.log("No competitor summary data found for this workspace");
          // Fallback to empty data
          this.competitorSummaryData = null;
          this.marketOverviewData = null;
          this.summaryRanks = [];
          this.competitors = [];
          this.allCompetitors = [];
          this.clearCaches();
          return;
        }

        // Sort dates to get the latest one
        const availableDates = datesSnapshot.docs
          .map((doc) => doc.id)
          .sort()
          .reverse();
        const latestDate = availableDates[0];

        // Fetch competitor summary data for the selected store using the latest date
        const storeDocRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "competitor-summary",
          latestDate,
          "stores",
          this.selectedLocationId
        );

        const storeDocSnap = await getDoc(storeDocRef);

        if (storeDocSnap.exists()) {
          const storeData = storeDocSnap.data();

          // Store the competitor summary data
          this.competitorSummaryData = storeData;

          // Store the market overview data
          this.marketOverviewData = storeData.marketOverview || null;
          this.marketOverviewData.competitorsOnly =
            storeData.competitorsOnly || null;

          // Store the pre-calculated summary ranks
          this.summaryRanks = storeData.summaryRanks || [];

          // Store the date for reference
          this.selectedDate = latestDate;

          // For backward compatibility, we still need to fetch individual competitors for the table
          // This is optional - you may want to store competitor details in the summary as well
          await this.fetchCompetitorsForTable();

          // Update caches based on new Firestore data
          this.setCachesFromFirestoreData();
        } else {
          console.log(
            `No competitor summary data found for store ${this.selectedLocationId} on date ${latestDate}`
          );
          // Fallback to empty data
          this.competitorSummaryData = null;
          this.marketOverviewData = null;
          this.summaryRanks = [];
          this.competitors = [];
          this.allCompetitors = [];
        }

        // Clear caches when new data is loaded
        this.clearCaches();
      } catch (error) {
        console.error("Error fetching competitor summary data:", error);
        throw error;
      }
    },

    async fetchCompetitorsForTable(loadMore = false) {
      // This method fetches individual competitor data for the detailed table with pagination
      const competitorsRef = collection(
        db,
        "users",
        this.currentUID,
        "fly_workspaces",
        this.selectedLocationId,
        "competitors"
      );

      // Build paginated query
      let paginatedQuery = query(
        competitorsRef,
        orderBy("rank", "asc"), // Order by rank for consistent pagination
        limit(this.competitorPagination.pageSize)
      );

      // If loading more pages, start after the last document
      if (loadMore && this.competitorPagination.lastDocument) {
        paginatedQuery = query(
          competitorsRef,
          orderBy("rank", "asc"),
          startAfter(this.competitorPagination.lastDocument),
          limit(this.competitorPagination.pageSize)
        );
      }

      // Set loading state
      if (loadMore) {
        this.competitorPagination.isLoadingMore = true;
      }

      try {
        const querySnapshot = await getDocs(paginatedQuery);
        const competitorsList = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Ensure icon/logo data is properly structured
          const competitorData = {
            id: doc.id,
            ...data,
            isCurrentBusiness: false, // Mark as competitor
            icon:
              data.icon ||
              data.logo ||
              data.logo_original ||
              data.thumbnail ||
              null,
          };
          competitorsList.push(competitorData);
        });

        // Update pagination state
        const isFirstLoad = !loadMore;
        if (isFirstLoad) {
          // First load - replace all data
          this.allCompetitors = [...competitorsList];
          this.competitors = [...competitorsList];
          this.competitorPagination.currentPage = 1;
        } else {
          // Loading more - append to existing data
          this.allCompetitors = [...this.allCompetitors, ...competitorsList];
          this.competitors = [...this.competitors, ...competitorsList];
          this.competitorPagination.currentPage++;
        }

        // Update pagination metadata
        this.competitorPagination.totalCompetitors = this.allCompetitors.length;
        this.competitorPagination.hasMore =
          querySnapshot.docs.length === this.competitorPagination.pageSize;
        this.competitorPagination.lastDocument =
          querySnapshot.docs[querySnapshot.docs.length - 1] || null;

        console.log(
          `Loaded ${competitorsList.length} competitors (Page ${this.competitorPagination.currentPage})`
        );
        console.log(
          `Total competitors: ${this.competitorPagination.totalCompetitors}`
        );
        console.log(`Has more: ${this.competitorPagination.hasMore}`);
      } catch (error) {
        console.error("Error fetching paginated competitors:", error);
        throw error;
      } finally {
        if (loadMore) {
          this.competitorPagination.isLoadingMore = false;
        }
      }
    },
    calculateSummaryRanks() {
      // Summary ranks are pre-calculated and already set in fetchCompetitors
      // This method is kept for compatibility but doesn't need to do calculations
      if (
        this.competitorSummaryData &&
        this.competitorSummaryData.summaryRanks
      ) {
        this.summaryRanks = this.competitorSummaryData.summaryRanks;
      }
      // Remove the fallback empty array assignment since it's already handled in fetchCompetitors
    },
    getCurrentBusinessData() {
      // Find the current business in the competitors list using place_id
      const currentPlaceId = this.currentBusinessPlaceId;
      if (!currentPlaceId) return null;

      // Look for the current business in the all competitors list
      const currentBusinessFromCompetitors = this.allCompetitors.find(
        (competitor) =>
          competitor.map_id_encoded === currentPlaceId ||
          competitor.place_id === currentPlaceId ||
          competitor.fid === currentPlaceId
      );

      if (currentBusinessFromCompetitors) {
        // Use the actual competitor data and mark it as current business
        return {
          ...currentBusinessFromCompetitors,
          isCurrentBusiness: true,
        };
      }

      // Fallback: construct from location data if not found in competitors
      const currentLocation = this.globalStores.find(
        (store) => store.id === this.selectedLocationId
      );
      if (!currentLocation) return null;

      return {
        isCurrentBusiness: true,
        title:
          currentLocation?.gmbProfile?.title ||
          currentLocation?.name ||
          "Your Business",
        rating:
          currentLocation?.gmbProfile?.rating ||
          currentLocation?.rating ||
          currentLocation?.average_rating ||
          0,
        reviews_cnt:
          currentLocation?.gmbProfile?.total_reviews ||
          currentLocation?.reviews_count ||
          currentLocation?.total_reviews ||
          0,
        category: this.parseCategories(currentLocation),
        tags: this.parseServiceTags(currentLocation),
        rank: this.calculateCurrentBusinessRank(currentLocation),
        image:
          currentLocation?.gmbProfile?.photo ||
          currentLocation?.logo_original ||
          currentLocation?.profile_image,
        phone:
          currentLocation?.gmbProfile?.phone ||
          currentLocation?.phone ||
          currentLocation?.phone_number,
        address:
          currentLocation?.gmbProfile?.address ||
          currentLocation?.address ||
          currentLocation?.formatted_address,
        open_hours:
          currentLocation?.gmbProfile?.hours ||
          currentLocation?.hours ||
          currentLocation?.business_hours,
        claimed: true, // Current business is always claimed
        place_id:
          currentLocation?.place_id || currentLocation?.gmbProfile?.place_id,
        map_id_encoded:
          currentLocation?.place_id || currentLocation?.gmbProfile?.place_id,
        latitude:
          currentLocation?.gmbProfile?.latitude ||
          currentLocation?.latitude ||
          currentLocation?.lat,
        longitude:
          currentLocation?.gmbProfile?.longitude ||
          currentLocation?.longitude ||
          currentLocation?.lng,
      };
    },
    parseCategories(location) {
      // Try to get categories from various possible sources
      const categories =
        location?.gmbProfile?.categories ||
        location?.categories ||
        location?.business_categories ||
        [];

      // Ensure categories are in the right format
      if (Array.isArray(categories)) {
        return categories.map((cat) => {
          if (typeof cat === "string") {
            return { title: cat, id: cat.toLowerCase().replace(/\s+/g, "_") };
          }
          return cat;
        });
      }
      return [];
    },
    parseServiceTags(location) {
      // Parse service tags from location data
      const services =
        location?.gmbProfile?.services ||
        location?.services ||
        location?.service_options ||
        [];

      if (Array.isArray(services)) {
        return services.map((service) => ({
          group_id: "service_options",
          value_title_short: service.title || service,
          value_title: service.title || service,
          key_title: service.title || service,
        }));
      }
      return [];
    },
    calculateCurrentBusinessRank(location) {
      // Try to determine a reasonable rank for the current business
      // This could be based on various factors from the business profile
      const rating = location?.gmbProfile?.rating || location?.rating || 0;
      const reviews =
        location?.gmbProfile?.total_reviews || location?.reviews_count || 0;

      // If we have actual rank data from competitors list, use it
      if (location?.rank && location.rank > 0) {
        return location.rank;
      }

      // Simple heuristic: better rating and more reviews = better rank
      const competitorCount = this.allCompetitors.length;
      if (rating >= 4.5 && reviews >= 50) return 1;
      if (rating >= 4.0 && reviews >= 20)
        return Math.floor(competitorCount * 0.2) || 1;
      if (rating >= 3.5 && reviews >= 10)
        return Math.floor(competitorCount * 0.4) || 1;

      // Default to middle of the pack
      return Math.floor(competitorCount * 0.6) || 1;
    },
    getAmenities(tags) {
      if (!Array.isArray(tags)) return [];
      return tags
        .filter((tag) => tag.value === 1) // Only include amenities that are available (value === 1)
        .map((tag) => tag.value_title_short || tag.value_title || tag.key_title)
        .filter(Boolean);
    },
    getRankColor(rank, total) {
      const percentage = rank / total;
      if (percentage <= 0.2) return "#16a34a"; // Muted green
      if (percentage <= 0.5) return "#ea580c"; // Muted orange
      return "#dc2626"; // Muted red
    },
    getRankTextColor(rank) {
      if (!rank) return "#757575";
      if (rank === 1) return "#2e7d32"; // Green for #1
      if (rank <= 3) return "#1565c0"; // Blue for top 3
      if (rank <= 5) return "#f57c00"; // Orange for top 5
      return "#d32f2f"; // Red for lower ranks
    },
    getReviewsTextColor(count) {
      if (count >= 100) return "#2e7d32"; // Green for many reviews
      if (count >= 20) return "#f57c00"; // Orange for moderate reviews
      return "#d32f2f"; // Red for few reviews
    },

    getRankDescription(category) {
      const descriptions = {
        "Overall Rank":
          "Weighted average ranking across all data points with emphasis on rating, reviews, search rank, and share of voice",
        "Search Rank": "Position in search results compared to competitors",
        Rating: "Star rating ranking (1-5 stars) compared to competitors",
        Reviews: "Total number of customer reviews compared to competitors",
        "Primary Category":
          "Whether the business is listed under the primary category",
        "Additional Categories":
          "Number of additional categories listed compared to competitors",
        Amenities:
          "Number of available amenities and features compared to competitors",
        "Share of Voice":
          "Percentage of total market reviews owned by this business",
      };
      return descriptions[category] || "Ranking compared to local competitors";
    },
    getMissingCategories() {
      const currentBusiness = this.getCurrentBusinessData();
      if (!currentBusiness) return "No business data available";

      const currentCategories = (currentBusiness.category || []).map(
        (cat) => cat.title
      );
      const competitorCategories = new Set();

      this.filteredCompetitors.forEach((comp) => {
        if (comp.category && Array.isArray(comp.category)) {
          comp.category.forEach((cat) => {
            if (cat.title && !currentCategories.includes(cat.title)) {
              competitorCategories.add(cat.title);
            }
          });
        }
      });

      if (competitorCategories.size === 0) {
        return "No additional categories found in competitor data";
      }

      return (
        Array.from(competitorCategories).slice(0, 5).join(", ") +
        (competitorCategories.size > 5
          ? ` (+${competitorCategories.size - 5} more)`
          : "")
      );
    },
    getMissingServices() {
      const currentBusiness = this.getCurrentBusinessData();
      if (!currentBusiness) return "No business data available";

      const currentServices = this.getAmenities(currentBusiness.tags);
      const competitorServices = new Set();

      this.filteredCompetitors.forEach((comp) => {
        const compServices = this.getAmenities(comp.tags);
        compServices.forEach((service) => {
          if (!currentServices.includes(service)) {
            competitorServices.add(service);
          }
        });
      });

      if (competitorServices.size === 0) {
        return "No additional services found in competitor data";
      }

      return (
        Array.from(competitorServices).slice(0, 5).join(", ") +
        (competitorServices.size > 5
          ? ` (+${competitorServices.size - 5} more)`
          : "")
      );
    },
    getRankPerformance(rank, total) {
      const percentage = rank / total;
      if (percentage <= 0.2) return "Excellent";
      if (percentage <= 0.5) return "Good";
      return "Needs Improvement";
    },
    getItemClass(item) {
      // Highlight current business if it matches place_id
      const isCurrentBusiness =
        this.currentBusinessPlaceId &&
        (item.map_id_encoded === this.currentBusinessPlaceId ||
          item.place_id === this.currentBusinessPlaceId ||
          item.isCurrentBusiness);

      return isCurrentBusiness ? "current-business-row" : "";
    },
    calculateShareOfVoice(business) {
      // Calculate the share of voice for a specific business
      const currentBusiness = this.getCurrentBusinessData();
      const allBusinesses = currentBusiness
        ? [currentBusiness, ...this.filteredCompetitors]
        : this.filteredCompetitors;

      const totalReviews = allBusinesses.reduce(
        (sum, item) => sum + (item.reviews_cnt || 0),
        0
      );
      const businessReviews = business.reviews_cnt || 0;

      return totalReviews > 0 ? (businessReviews / totalReviews) * 100 : 0;
    },
    calculateDistances() {
      // Calculate distances between current business and competitors
      const currentBusiness = this.getCurrentBusinessData();

      if (
        !currentBusiness ||
        !currentBusiness.latitude ||
        !currentBusiness.longitude
      ) {
        return {
          nearest: "N/A",
          farthest: "N/A",
        };
      }

      const distances = [];

      this.filteredCompetitors.forEach((competitor) => {
        if (competitor.latitude && competitor.longitude) {
          const distance = this.calculateDistance(
            currentBusiness.latitude,
            currentBusiness.longitude,
            competitor.latitude,
            competitor.longitude
          );
          distances.push(distance);
        }
      });

      if (distances.length === 0) {
        return {
          nearest: "N/A",
          farthest: "N/A",
        };
      }

      const nearest = Math.min(...distances);
      const farthest = Math.max(...distances);

      return {
        nearest: `${nearest.toFixed(2)} km`,
        farthest: `${farthest.toFixed(2)} km`,
      };
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula to calculate distance between two points
      const R = 6371; // Radius of the Earth in kilometers
      const dLat = this.degreesToRadians(lat2 - lat1);
      const dLon = this.degreesToRadians(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.degreesToRadians(lat1)) *
          Math.cos(this.degreesToRadians(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c; // Distance in kilometers
    },
    degreesToRadians(degrees) {
      return degrees * (Math.PI / 180);
    },
    getTotalMarketReviews() {
      const currentBusiness = this.getCurrentBusinessData();
      const allBusinesses = currentBusiness
        ? [currentBusiness, ...this.filteredCompetitors]
        : this.filteredCompetitors;
      return allBusinesses.reduce(
        (sum, item) => sum + (item.reviews_cnt || 0),
        0
      );
    },
    calculateOverallRankForBusiness(targetBusiness, allBusinesses) {
      // Get or calculate the overall rankings for all businesses
      const overallRankings = this.calculateAllOverallRanks(allBusinesses);

      // Find the target business in the rankings
      const targetRanking = overallRankings.find((ranking) => {
        return this.isSameBusiness(ranking.business, targetBusiness);
      });

      return targetRanking ? targetRanking.rank : allBusinesses.length;
    },
    calculateAllOverallRanks(allBusinesses) {
      // Define the same categories and weights as in summary calculation
      const categories = [
        {
          getValue: (item) => item.rating || 0,
          descending: true,
          weight: 3,
        },
        {
          getValue: (item) => item.reviews_cnt || 0,
          descending: true,
          weight: 2,
        },
        {
          getValue: (item) => this.calculateShareOfVoice(item),
          descending: true,
          weight: 3,
        },
        {
          getValue: (item) => item.rank || 999,
          descending: false,
          weight: 3,
        },
        {
          getValue: (item) =>
            item.category && item.category.length > 0 ? 1 : 0,
          descending: true,
          weight: 1,
        },
        {
          getValue: (item) =>
            item.category ? Math.max(0, item.category.length - 1) : 0,
          descending: true,
          weight: 1,
        },
        {
          getValue: (item) => this.getAmenities(item.tags).length,
          descending: true,
          weight: 1,
        },
      ];

      // Calculate weighted score for each business
      const businessScores = allBusinesses.map((business) => {
        // Calculate individual category ranks for this business
        const categoryRanks = categories.map((cat) => {
          const sorted = [...allBusinesses].sort((a, b) => {
            const aVal = cat.getValue(a);
            const bVal = cat.getValue(b);
            return cat.descending ? bVal - aVal : aVal - bVal;
          });

          const businessRank =
            sorted.findIndex((item) => this.isSameBusiness(item, business)) + 1;

          return {
            rank: businessRank > 0 ? businessRank : sorted.length,
            total: sorted.length,
            weight: cat.weight,
          };
        });

        // Calculate weighted score (lower is better)
        const totalWeight = categoryRanks.reduce(
          (sum, rank) => sum + rank.weight,
          0
        );
        const weightedSum = categoryRanks.reduce((sum, rank) => {
          const rankPercentage = rank.rank / rank.total;
          return sum + rankPercentage * rank.weight;
        }, 0);
        const weightedScore = weightedSum / totalWeight;

        return {
          business: business,
          weightedScore: weightedScore,
        };
      });

      // Sort by weighted score (lower is better) and assign ranks 1, 2, 3, etc.
      const sortedBusinesses = businessScores.sort(
        (a, b) => a.weightedScore - b.weightedScore
      );

      return sortedBusinesses.map((item, index) => ({
        business: item.business,
        rank: index + 1, // Ranks start from 1
        weightedScore: item.weightedScore,
      }));
    },
    isSameBusiness(item, target) {
      // Check if both are current business
      if (item.isCurrentBusiness && target.isCurrentBusiness) return true;

      // Check place_id match
      if (item.place_id && target.place_id && item.place_id === target.place_id)
        return true;
      if (
        item.map_id_encoded &&
        target.map_id_encoded &&
        item.map_id_encoded === target.map_id_encoded
      )
        return true;

      // Check title match (fallback)
      if (item.title && target.title && item.title === target.title)
        return true;

      return false;
    },
    customSort(items, sortBy, sortDesc) {
      // Custom sort that always keeps current business at the top
      return items.sort((a, b) => {
        // Always put current business first
        if (a.isCurrentBusiness && !b.isCurrentBusiness) return -1;
        if (!a.isCurrentBusiness && b.isCurrentBusiness) return 1;

        // If both are current business or both are competitors, use normal sorting
        if (sortBy.length === 0) return 0;

        for (let i = 0; i < sortBy.length; i++) {
          const sortKey = sortBy[i];
          const isDesc = sortDesc[i];

          let aVal = a[sortKey];
          let bVal = b[sortKey];

          // Handle special cases
          if (sortKey === "share_of_voice") {
            aVal = a.share_of_voice || 0;
            bVal = b.share_of_voice || 0;
          } else if (sortKey === "overall_rank") {
            aVal = a.overall_rank || 999;
            bVal = b.overall_rank || 999;
          } else if (sortKey === "reviews_cnt") {
            aVal = a.reviews_cnt || 0;
            bVal = b.reviews_cnt || 0;
          } else if (sortKey === "rating") {
            aVal = a.rating || 0;
            bVal = b.rating || 0;
          } else if (sortKey === "rank") {
            aVal = a.rank || 999;
            bVal = b.rank || 999;
          }

          // Convert to strings for comparison if needed
          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return isDesc ? 1 : -1;
          if (aVal > bVal) return isDesc ? -1 : 1;
        }

        return 0;
      });
    },
    handleImageError(event, item) {
      // Prevent infinite error loops
      if (item.usingPlaceholder) {
        return;
      }

      // Try fallback images in order
      if (item.logo && event.target.src !== item.logo) {
        event.target.src = item.logo;
      } else if (
        item.logo_original &&
        event.target.src !== item.logo_original
      ) {
        event.target.src = item.logo_original;
      } else if (item.thumbnail && event.target.src !== item.thumbnail) {
        event.target.src = item.thumbnail;
      } else {
        // Use the placeholder image as final fallback
        event.target.src = this.businessPlaceholder;
        // Mark this item as having used the placeholder
        this.$set(item, "usingPlaceholder", true);
      }
    },
    async loadMoreCompetitors() {
      if (
        !this.competitorPagination.hasMore ||
        this.competitorPagination.isLoadingMore
      ) {
        return;
      }

      try {
        // Fetch next batch of competitors
        await this.fetchCompetitorsForTable(true);

        // Only update table data incrementally instead of full recalculation
        await this.updateTableDataIncremental();

        // Continue loading progressively if more data is available
        if (this.competitorPagination.hasMore) {
          // Add a small delay to prevent overwhelming the server
          setTimeout(() => {
            this.loadMoreCompetitors();
          }, 800); // Reduced to 800ms for faster loading
        }
      } catch (error) {
        console.error("Error loading more competitors:", error);
        // Stop loading on error
        this.competitorPagination.hasMore = false;
        this.competitorPagination.isLoadingMore = false;
      }
    },

    async updateTableDataIncremental() {
      // Use lightweight processing to avoid UI blocking
      const newCompetitors = this.getNewCompetitorsLightweight();

      if (newCompetitors.length === 0) return;

      // Filter out any competitors that already exist in table data to prevent duplicates
      const filteredNewCompetitors =
        this.removeDuplicateCompetitors(newCompetitors);

      if (filteredNewCompetitors.length === 0) return;

      // Process competitors in small chunks to avoid blocking UI
      await this.processCompetitorsInChunks(filteredNewCompetitors);
    },

    removeDuplicateCompetitors(newCompetitors) {
      // Get existing IDs from table data to prevent duplicates
      const existingIds = new Set();

      this.deferredTableData.forEach((item) => {
        // Use multiple potential ID fields to catch duplicates
        if (item.id) existingIds.add(item.id);
        if (item.place_id) existingIds.add(item.place_id);
        if (item.map_id_encoded) existingIds.add(item.map_id_encoded);
        if (item.fid) existingIds.add(item.fid);
      });

      // Filter out competitors that already exist
      return newCompetitors.filter((comp) => {
        const hasId = comp.id && existingIds.has(comp.id);
        const hasPlaceId = comp.place_id && existingIds.has(comp.place_id);
        const hasMapId =
          comp.map_id_encoded && existingIds.has(comp.map_id_encoded);
        const hasFid = comp.fid && existingIds.has(comp.fid);

        // Return true if this competitor is NOT already in the table
        return !(hasId || hasPlaceId || hasMapId || hasFid);
      });
    },

    getNewCompetitorsLightweight() {
      // Get the actual number of competitors currently in the table (excluding current business)
      const currentBusinessInTable = this.deferredTableData.some(
        (item) => item.isCurrentBusiness
      );
      const tableCompetitorCount =
        this.deferredTableData.length - (currentBusinessInTable ? 1 : 0);

      // Get competitors that haven't been added to the table yet
      const allCompetitorData = this.competitors.filter(
        (comp) => !comp.isCurrentBusiness
      );

      return allCompetitorData.slice(tableCompetitorCount);
    },

    async processCompetitorsInChunks(newCompetitors) {
      const CHUNK_SIZE = 5; // Process 5 competitors at a time

      for (let i = 0; i < newCompetitors.length; i += CHUNK_SIZE) {
        const chunk = newCompetitors.slice(i, i + CHUNK_SIZE);

        // Process chunk with lightweight calculations
        const processedChunk = chunk.map((comp) => ({
          ...comp,
          // Use simple, fast calculations instead of heavy ones
          share_of_voice: this.calculateShareOfVoiceFast(comp),
          overall_rank: this.calculateRankFast(comp),
        }));

        // Add to table data
        this.deferredTableData = [...this.deferredTableData, ...processedChunk];

        // Yield control back to UI thread between chunks
        await this.yieldToUI();
      }
    },

    async yieldToUI() {
      // Allow UI to update between processing chunks
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 0);
        });
      });
    },

    calculateShareOfVoiceFast(competitor) {
      // Fast approximation without heavy recalculation
      const reviews = competitor.reviews_cnt || 0;
      const avgMarketReviews = 50; // Reasonable approximation
      const totalCompetitors = Math.max(this.competitors.length, 10);
      const estimatedTotalReviews = avgMarketReviews * totalCompetitors;

      return estimatedTotalReviews > 0
        ? (reviews / estimatedTotalReviews) * 100
        : 0;
    },

    calculateRankFast(competitor) {
      // Fast ranking based on simple metrics
      const rating = competitor.rating || 0;
      const reviews = competitor.reviews_cnt || 0;
      const searchRank = competitor.rank || 999;

      // Simple scoring algorithm (higher score = better rank)
      const score =
        rating * 20 + Math.min(reviews, 100) * 0.5 + (1000 - searchRank);

      // Estimate rank based on score (this is approximate, not exact)
      const totalCompetitors = this.competitors.length;
      const percentile = Math.max(0, Math.min(1, score / 1200)); // Normalize to 0-1
      const estimatedRank = Math.ceil((1 - percentile) * totalCompetitors);

      return Math.max(1, estimatedRank);
    },

    resetPagination() {
      this.competitorPagination = {
        pageSize: 50,
        currentPage: 1,
        totalPages: 1,
        totalCompetitors: 0,
        hasMore: true,
        lastDocument: null,
        isLoadingMore: false,
      };
    },

    // Performance optimized search with debouncing
    onSearchInput(searchValue) {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce search input
      this.searchTimeout = setTimeout(() => {
        this.searchQuery = searchValue;
        this.updateFilteredData();
      }, this.searchDebounceMs);
    },

    // Optimized filtering with memoization
    updateFilteredData() {
      // Create hash for current filter state
      const filterHash = JSON.stringify({
        categories: this.selectedCategories,
        subCategories: this.selectedSubCategories,
        distance: this.maxDistance,
        search: this.searchQuery,
        totalCompetitors: this.competitors.length,
      });

      // Return cached result if filters haven't changed
      if (filterHash === this.memoizedFilters.lastFilterHash) {
        return this.memoizedFilters.lastFilteredResult;
      }

      // Apply all filters efficiently
      let filtered = this.applyFiltersOptimized();

      // Cache the result
      this.memoizedFilters.lastFilterHash = filterHash;
      this.memoizedFilters.lastFilteredResult = filtered;

      return filtered;
    },

    applyFiltersOptimized() {
      // Use optimized filtering approach
      const currentPlaceId = this.currentBusinessPlaceId;

      return this.competitors.filter((comp) => {
        // Exclude current business
        if (
          currentPlaceId &&
          (comp.map_id_encoded === currentPlaceId ||
            comp.place_id === currentPlaceId ||
            comp.fid === currentPlaceId)
        ) {
          return false;
        }

        // Distance filter (most expensive, do last)
        if (this.maxDistance) {
          const currentBusiness = this.getCurrentBusinessData();
          if (
            currentBusiness?.latitude &&
            currentBusiness?.longitude &&
            comp.latitude &&
            comp.longitude
          ) {
            const distance = this.getCachedDistance(currentBusiness, comp);
            if (distance > this.maxDistance) return false;
          } else {
            return false;
          }
        }

        // Category filters
        if (this.selectedCategories.length > 0) {
          if (
            !comp.category?.some((cat) =>
              this.selectedCategories.includes(
                cat.id || cat.title.toLowerCase().replace(/\s+/g, "_")
              )
            )
          ) {
            return false;
          }
        }

        // Sub-category filters
        if (this.selectedSubCategories.length > 0) {
          if (
            !comp.category?.some((cat) =>
              this.selectedSubCategories.includes(
                cat.id || cat.title.toLowerCase().replace(/\s+/g, "_")
              )
            )
          ) {
            return false;
          }
        }

        // Search query (do last as it's string-intensive)
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          return (
            comp.title?.toLowerCase().includes(query) ||
            comp.address?.toLowerCase().includes(query) ||
            comp.category?.some((cat) =>
              cat.title?.toLowerCase().includes(query)
            )
          );
        }

        return true;
      });
    },
  },
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Location dropdown styling */
.location-input {
  width: 100% !important;
}

/* Rank card styling */
.rank-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rank-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.rank-info-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.rank-info-icon:hover {
  opacity: 1;
}

/* Tooltip styling */
.tooltip-content {
  max-width: 350px;
  text-align: left;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
}

/* Tooltip list styling */
.tooltip-content .mb-1 {
  margin-bottom: 4px !important;
}

.tooltip-content .mb-2 {
  margin-bottom: 8px !important;
}

/* Current business row highlighting */
.current-business-row {
  background-color: #f8fafc !important;
  border-left: 4px solid #64748b !important;
  font-weight: 500 !important;
}

.current-business-row:hover {
  background-color: #f1f5f9 !important;
}

/* Current business chip styling */
.v-chip.v-size--x-small {
  height: 18px !important;
  font-size: 10px !important;
}

/* Filter chip styling */
.v-chip.v-chip--selected {
  background-color: #64748b !important;
  color: white !important;
}

/* Outlined filter chips */
.v-chip--outlined {
  border: 1.5px solid !important;
  font-weight: 500 !important;
}

.v-chip--outlined.primary--text {
  border-color: #1976d2 !important;
  color: #1976d2 !important;
}

/* Dropdown styling */
.absolute {
  z-index: 1000;
}

/* Table improvements */
::v-deep .v-data-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

::v-deep .v-data-table thead th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
}

/* Disable interaction during processing */
.filter-processing ::v-deep .v-select--is-disabled .v-input__control {
  opacity: 0.7;
}

.filter-processing ::v-deep .v-btn--disabled {
  opacity: 0.5 !important;
}

/* Category chip styling */
.primary-category-chip {
  background-color: #e3f2fd !important; /* Light blue background */
  border-color: #2196f3 !important; /* Blue border */
  color: #1976d2 !important; /* Blue text */
  font-weight: 600 !important;
}

.secondary-category-chip {
  background-color: #f5f5f5 !important; /* Light gray background */
  border-color: #9e9e9e !important; /* Gray border */
  color: #616161 !important; /* Dark gray text */
  font-weight: 500 !important;
}

/* Loading indicator styling */
.loading-indicator {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}
</style>
